import { z } from 'zod';
/**
 * `urlKey` (slug) de notas y secciones: lo que va en la URL pública.
 *
 * Restringido a minúsculas, números y guiones porque forma parte de URLs canónicas: un
 * slug con mayúsculas o caracteres raros genera duplicados de contenido para el crawler.
 */
export const UrlKeySchema = z
    .string()
    .min(1)
    .max(160)
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'kebab-case (a-z, 0-9 y guiones simples)');
/**
 * Los `urlKey` que ya son una **página del sitio** y que por eso una sección no puede usar.
 *
 * El sitio resuelve `/algo` con una ruta comodín de un segmento (`/:categoria`), y las rutas
 * estáticas le ganan. Así que una categoría con uno de estos `urlKey` queda con su página
 * **inalcanzable**: la URL existe, devuelve 200, y muestra otra cosa. No falla nada, no hay error en
 * ningún log, y desde el panel se ve una sección perfectamente normal.
 *
 * No es hipotético: "Última hora" es un nombre de sección de lo más común en un diario, y deriva
 * justo en `ultima-hora`.
 *
 * La lista vive acá y no en el sitio ni en la API porque la usan los tres: el sitio la tiene como
 * rutas, la API rechaza el alta, y el panel avisa mientras se escribe el nombre. **Si se agrega una
 * página estática de un segmento, va acá el mismo día** — si no, la próxima colisión vuelve a ser
 * invisible.
 */
export const URL_KEYS_RESERVADAS = ['buscar', 'ultima-hora', 'mas-leidas'];
export function esUrlKeyReservada(urlKey) {
    return URL_KEYS_RESERVADAS.includes(urlKey);
}
/** Saca los acentos: "Récord" → "Record". Base de `toUrlKey` y de la búsqueda. */
export function sinAcentos(input) {
    return input.normalize('NFD').replace(/[̀-ͯ]/g, '');
}
/** Normaliza un título a `urlKey` (sin acentos, kebab-case). */
export function toUrlKey(input) {
    return sinAcentos(input)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-') // deja a lo sumo un guión por separador
        .replace(/^-/, '')
        .replace(/-$/, '')
        .slice(0, 160);
}
/**
 * Forma comparable de un texto: sin acentos, en minúsculas y con los espacios colapsados.
 *
 * Es lo que se guarda en `articles.title_normalized` y lo que se le aplica a lo que escribe la
 * persona en el buscador, para que "record" encuentre "Récord" y "MAÑANA" encuentre "manana".
 * Buscar así **no** se puede resolver desde el lado del cliente ni con `mode: 'insensitive'` de
 * Prisma: Postgres considera `é` y `e` letras distintas, y la extensión `unaccent` necesita
 * superusuario para instalarse (el rol de la app no lo es, a propósito).
 */
export function normalizarBusqueda(input) {
    return sinAcentos(input).toLowerCase().replace(/\s+/g, ' ').trim();
}
